import axios, { type AxiosRequestConfig } from "axios";
import { parseAppEnvironment, validateBackendForEnvironment } from "../config/environment";

export type ApiErrorCategory =
  | "configuration"
  | "timeout"
  | "network"
  | "http"
  | "unexpected";

export class ApiError extends Error {
  category: ApiErrorCategory;
  status?: number;
  detail?: unknown;

  constructor(
    message: string,
    category: ApiErrorCategory,
    status?: number,
    detail?: unknown
  ) {
    super(message);
    this.name = "ApiError";
    this.category = category;
    this.status = status;
    this.detail = detail;
  }
}

const CONFIGURATION_MESSAGE =
  "REACT_APP_BACKEND_URL doit être une URL HTTP(S) absolue ou /api.";

export function normalizeApiBaseUrl(value: string | undefined): string {
  if (!value || value.trim() === "" || value !== value.trim()) {
    throw new ApiError(CONFIGURATION_MESSAGE, "configuration");
  }

  if (/^\/api\/+$/i.test(value) || value.toLowerCase() === "/api") {
    return "/api";
  }

  let parsed: URL;
  try {
    parsed = new URL(value);
  } catch {
    throw new ApiError(CONFIGURATION_MESSAGE, "configuration");
  }

  if (
    !["http:", "https:"].includes(parsed.protocol) ||
    !parsed.hostname ||
    parsed.username ||
    parsed.password ||
    parsed.search ||
    parsed.hash
  ) {
    throw new ApiError(CONFIGURATION_MESSAGE, "configuration");
  }

  return value.replace(/\/+$/, "");
}

let configurationError: ApiError | null = null;
let configuredBaseUrl = "";

try {
  configuredBaseUrl = normalizeApiBaseUrl(
    process.env.REACT_APP_BACKEND_URL
  );
  validateBackendForEnvironment(
    parseAppEnvironment(process.env.REACT_APP_APP_ENV), configuredBaseUrl,
  );
} catch (error) {
  configurationError = error as ApiError;
}

// Base URL of the existing FastAPI backend. An invalid value is retained as a
// configuration error and rejected before any network request is attempted.
export const API_BASE_URL = configuredBaseUrl;

export const http = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
  headers: { "Content-Type": "application/json" },
});

type AccessTokenProvider = () => Promise<string | null>;

let accessTokenProvider: AccessTokenProvider | null = null;
let authenticationFailureHandler: (() => void) | null = null;

// Configuré uniquement par AuthProvider après l'initialisation MSAL. Toutes les
// requêtes applicatives passent par cette frontière afin qu'aucune page ne
// manipule ou ne persiste elle-même un jeton Entra.
export function setAccessTokenProvider(provider: AccessTokenProvider | null): void {
  accessTokenProvider = provider;
}

export function setAuthenticationFailureHandler(handler: (() => void) | null): void {
  authenticationFailureHandler = handler;
}

export function toApiError(error: unknown): ApiError {
  if (axios.isAxiosError<{ detail?: unknown }>(error)) {
    const detail = error.response?.data?.detail;

    if (error.code === "ECONNABORTED" || error.code === "ETIMEDOUT") {
      return new ApiError(
        "Le délai d'attente de la requête est dépassé.",
        "timeout",
        error.response?.status,
        detail
      );
    }

    if (error.response) {
      const message =
        typeof detail === "object" && detail !== null && "message" in detail
          ? String(detail.message)
          : null;
      return new ApiError(
        typeof detail === "string" ? detail : message ?? `HTTP ${error.response.status}`,
        "http",
        error.response.status,
        detail
      );
    }

    return new ApiError(
      "Impossible de joindre le backend.",
      "network",
      undefined,
      detail
    );
  }

  return new ApiError(
    error instanceof Error ? error.message : "Erreur inattendue.",
    "unexpected"
  );
}

// Single centralized request layer. Pages MUST NOT call axios/fetch directly.
export async function request<T>(config: AxiosRequestConfig): Promise<T> {
  if (configurationError) {
    throw configurationError;
  }

  try {
    const token = accessTokenProvider ? await accessTokenProvider() : null;
    const authenticatedConfig: AxiosRequestConfig = token
      ? {
          ...config,
          headers: { ...config.headers, Authorization: `Bearer ${token}` },
        }
      : config;
    const response = await http.request<T>(authenticatedConfig);
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 401) {
      authenticationFailureHandler?.();
    }
    throw toApiError(error);
  }
}

export const apiGet = <T,>(url: string, params?: Record<string, unknown>) =>
  request<T>({ method: "GET", url, params });

export const apiPost = <T,>(url: string, data?: unknown) =>
  request<T>({ method: "POST", url, data });

export const apiPatch = <T,>(url: string, data?: unknown) =>
  request<T>({ method: "PATCH", url, data });

export const apiPut = <T,>(url: string, data?: unknown) =>
  request<T>({ method: "PUT", url, data });

export const apiDelete = <T,>(url: string) =>
  request<T>({ method: "DELETE", url });
